import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Layout from '../layout/Layout';
import LoadingSpinner from '../common/LoadingSpinner';
import toast from 'react-hot-toast';
import { leadStagesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const LeadStagesMaster = () => {
  const [leadStages, setLeadStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    order: 0,
    probability: 0
  });
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingStage, setDeletingStage] = useState(null);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchLeadStages();
  }, []);

  const fetchLeadStages = async () => {
    try {
      setLoading(true);
      const apiToUse = isSuperAdmin ? leadStagesAPI.getMaster : leadStagesAPI.getAll;
      const response = await apiToUse();
      setLeadStages(response.data.leadStages || []);
    } catch (error) {
      toast.error('Failed to load lead stages');
      console.error('Fetch lead stages error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Stage name is required');
      return;
    }

    try {
      setSubmitting(true);
      if (editingStage) {
        const updateApi = isSuperAdmin ? leadStagesAPI.updateMaster : leadStagesAPI.update;
        await updateApi(editingStage._id, formData);
        toast.success('Lead stage updated successfully');
      } else {
        const createApi = isSuperAdmin ? leadStagesAPI.createMaster : leadStagesAPI.create;
        await createApi(formData);
        toast.success('Lead stage created successfully');
      }

      setShowModal(false);
      setEditingStage(null);
      setFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        order: 0,
        probability: 0
      });
      fetchLeadStages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save lead stage');
      console.error('Save lead stage error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (stage) => {
    setEditingStage(stage);
    setFormData({
      name: stage.name,
      description: stage.description || '',
      color: stage.color,
      order: stage.order,
      probability: stage.probability || 0
    });
    setShowModal(true);
  };

  const handleDelete = (stage) => {
    setDeletingStage(stage);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingStage) return;

    try {
      if (isSuperAdmin) {
        await leadStagesAPI.deleteMaster(deletingStage._id);
      } else {
        await leadStagesAPI.delete(deletingStage._id);
      }
      toast.success('Lead stage deleted successfully');
      fetchLeadStages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete lead stage');
      console.error('Delete lead stage error:', error);
    } finally {
      setShowDeleteModal(false);
      setDeletingStage(null);
    }
  };

  const filteredStages = leadStages.filter(stage =>
    stage.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading lead stages..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6">
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-6 md:mb-8 overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 p-4 md:p-8 text-white shadow-2xl"
        >
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-white/5 blur-2xl"></div>

          <div className="relative z-10">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center justify-between"
            >
              <div>
                <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                  Lead Stages
                </h1>
                <p className="text-blue-100 mt-2 text-sm md:text-lg">
                  Manage and organize your sales pipeline stages
                </p>
              </div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                className="hidden md:block"
              >
                <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl shadow-xl">
                  <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Search and Add Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between"
        >
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search lead stages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm transition-all duration-200"
            />
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setEditingStage(null);
              setFormData({ name: '', description: '', color: '#3B82F6', order: 0, probability: 0 });
              setShowModal(true);
            }}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Lead Stage
          </motion.button>
        </motion.div>

        {/* Lead Stages Grid */}
        {filteredStages.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredStages.map((stage, index) => (
              <motion.div
                key={stage._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ y: -4 }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full shadow-sm border-2 border-white"
                        style={{ backgroundColor: stage.color }}
                      ></div>
                      <h3 className="text-lg font-bold text-gray-900">{stage.name}</h3>
                    </div>
                    <div className="flex items-center space-x-1">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleEdit(stage)}
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                        title="Edit stage"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleDelete(stage)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete stage"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </motion.button>
                    </div>
                  </div>

                  {stage.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{stage.description}</p>
                  )}

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Order</span>
                      <span className="font-semibold text-gray-900">{stage.order}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Probability</span>
                      <span className="font-semibold text-gray-900">{stage.probability || 0}%</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Status</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        stage.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {stage.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Created {new Date(stage.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mb-6">
              <PlusIcon className="h-12 w-12 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? 'No stages found' : 'No lead stages yet'}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchTerm
                ? 'Try adjusting your search terms to find what you\'re looking for.'
                : 'Get started by creating your first lead stage to organize your sales pipeline.'
              }
            </p>
            {!searchTerm && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setEditingStage(null);
                  setFormData({ name: '', description: '', color: '#3B82F6', order: 0, probability: 0 });
                  setShowModal(true);
                }}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create First Stage
              </motion.button>
            )}
          </motion.div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
              onClick={() => setShowModal(false)}
            >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <form onSubmit={handleSubmit}>
                    <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 px-6 py-5 relative overflow-hidden">
                      <div className="absolute inset-0 bg-black/10"></div>
                      <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
                      <div className="relative z-10 flex items-center justify-between">
                        <h3 className="text-xl font-bold text-white">
                          {editingStage ? 'Edit Lead Stage' : 'Create New Stage'}
                        </h3>
                        <motion.button
                          whileHover={{ scale: 1.1, rotate: 90 }}
                          whileTap={{ scale: 0.9 }}
                          type="button"
                          onClick={() => {
                            setShowModal(false);
                            setEditingStage(null);
                            setFormData({ name: '', description: '', color: '#3B82F6', order: 0, probability: 0 });
                          }}
                          className="p-2 hover:bg-white/20 rounded-xl transition-colors duration-200"
                        >
                          <XMarkIcon className="h-6 w-6 text-white" />
                        </motion.button>
                      </div>
                    </div>

                    <div className="p-6 space-y-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-3">
                          Stage Name *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm hover:bg-white"
                          placeholder="Enter stage name"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-3">
                          Stage Color *
                        </label>
                        <div className="flex items-center space-x-4">
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="relative"
                          >
                            <input
                              type="color"
                              value={formData.color}
                              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                              className="w-12 h-12 border-2 border-gray-200 rounded-xl cursor-pointer shadow-sm"
                            />
                          </motion.div>
                          <div className="flex-1">
                            <input
                              type="text"
                              value={formData.color}
                              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm font-mono text-sm hover:bg-white"
                              placeholder="#3B82F6"
                              pattern="^#[0-9A-Fa-f]{6}$"
                              title="Enter a valid hex color code (e.g., #FF0000)"
                            />
                          </div>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                          Choose a color to visually represent this stage
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-3">
                          Description
                        </label>
                        <textarea
                          value={formData.description || ''}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm hover:bg-white resize-none"
                          placeholder="Describe this lead stage..."
                        />
                        <p className="mt-2 text-sm text-gray-600">
                          Optional description for this stage
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-3">
                            Order
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={formData.order || 0}
                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm hover:bg-white"
                            placeholder="0"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-3">
                            Probability (%)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.probability || 0}
                            onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) || 0 })}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 shadow-sm hover:bg-white"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 px-6 py-4 rounded-b-3xl flex justify-end space-x-3 border-t border-gray-100">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="button"
                        onClick={() => {
                          setShowModal(false);
                          setEditingStage(null);
                          setFormData({ name: '', description: '', color: '#3B82F6', order: 0, probability: 0 });
                        }}
                        className="px-6 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-100 transition-all duration-200 font-semibold shadow-sm"
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                      >
                        {submitting ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Saving...
                          </div>
                        ) : (
                          editingStage ? 'Update Stage' : 'Create Stage'
                        )}
                      </motion.button>
                    </div>
                  </form>
                </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteModal && deletingStage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
              onClick={() => setShowDeleteModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl shadow-2xl max-w-md w-full border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                    <TrashIcon className="w-8 h-8 text-red-600" />
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                    Delete Lead Stage
                  </h3>

                  <p className="text-gray-600 text-center mb-6">
                    Are you sure you want to delete <span className="font-semibold text-gray-900">"{deletingStage.name}"</span>?
                    This action cannot be undone.
                  </p>

                  <div className="flex space-x-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDeletingStage(null);
                      }}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={confirmDelete}
                      className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold shadow-lg"
                    >
                      Delete
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
};

export default LeadStagesMaster;